<template lang="pug">
  .b-crm-kanban
    button._scroll-btn.-left(
      type="button"
      :disabled="!canScrollLeft"
      title="Прокрутить назад"
      @click="scroll(-1)"
      )
      Icon(name="arr-scroll" pointer colored)
    ._viewport(ref="viewport" @scroll="updateScrollState" @dragend="onBoardDragEnd")
      ._board(ref="board")
        ._stage(v-for="stage in stages" :key="stage.id" ref="stage")
          ._stage-header
            //- длинное название обрезается троеточием, целиком — в подсказке при наведении
            span._stage-title(:title="stage.name") {{ stage.name }}
            span._stage-count {{ leadCountLabel(stageLeads(stage.id).length) }}
          ._stage-line(:style="{ backgroundColor: stage.color }")
          //- .kanban-stage-drop — хук-класс на корне DraggableList: колонку и
          //- список под ней растягиваем на всю высоту доски (см. стили)
          DraggableList(
            :ref="'draggableList-' + stage.id"
            class="kanban-stage-drop"
            :itemsRaw="stageLeads(stage.id)"
            :canReceive="true"
            type="lead"
            v-slot="{ items, onDragStart, onDragEnd, onDrop, draggableItem }"
            @change="onColumnChange(stage.id, $event)"
            )
            //- Зона приёма — весь список целиком, а не только карточки в нём:
            //- бросать заявку можно в любое место колонки. .self, чтобы
            //- всплывшие с карточки события не перебивали её собственный
            //- индекс вставки и не роняли второй onDrop
            ._stage-list(
            @dragenter.self="setHoverIndex(stage.id, items.length, draggableItem)"
            @dragover.prevent
            @drop.self.prevent="onDrop()"
            )
              ._lead(
              v-for="(lead, index) in items"
              :key="lead.id"
              :class="{ drag: draggableItem === lead }"
              draggable
              @dragstart="onDragStart($event, lead)"
              @dragenter="setHoverIndex(stage.id, index, draggableItem)"
              @dragover.prevent
              @dragend="onDragEnd(lead)"
              @drop="onDrop(lead)"
              )
                CrmTileLeadUi(
                :lead="lead"
                @open="$emit('openLead', lead)"
                @remove="$emit('removeLead', lead)"
                )
    button._scroll-btn.-right(
      type="button"
      :disabled="!canScrollRight"
      title="Прокрутить вперёд"
      @click="scroll(1)"
      )
      Icon(name="arr-scroll" pointer colored)
</template>

<script>
import CrmTileLeadUi from '@/components/crm/CrmTileLeadUi.vue';
import DraggableList from '@/components/draggable/DraggableList.vue';
import enableTouchDrag, { isTouchDragging } from '@/utils/touchDrag';
import utils from '@/utils/common';

export default {
  name: 'CrmKanbanUi',
  components: { CrmTileLeadUi, DraggableList },
  props: {
    stages: {
      type: Array,
      required: true,
    },
    leads: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      // which stage the dragged lead is currently hovering over
      hoveredStageId: null,
      // колонка, из которой взяли карточку: пока её тащат пальцем, эта
      // колонка не перерисовывается — см. isStageFrozen
      draggedStageId: null,
    };
  },
  watch: {
    stages() { this.$nextTick(this.updateScrollState); },
    leads() { this.$nextTick(this.updateScrollState); },
    // DraggableList keeps its own drop position (draggableIndex) once a
    // column is entered, with no way to clear it short of leaving that
    // column — so once a drag has passed through several columns, every one
    // of them still renders the dragged lead as a ghost card. Since we can't
    // modify DraggableList itself, reset the previous column's index by
    // reaching into its instance directly whenever the hovered stage changes.
    hoveredStageId(newStageId, oldStageId) {
      if (oldStageId === null || oldStageId === newStageId) return;
      if (this.isStageFrozen(oldStageId)) return;
      // a ref inside a v-for ancestor is always an array in Vue 2, even with
      // a unique dynamic ref name per iteration — so this is a 1-item array
      const [prevList] = this.$refs[`draggableList-${oldStageId}`] ?? [];
      if (prevList) prevList.draggableIndex = null;
    },
  },
  created() {
    // Один экземпляр на всё время жизни: слушатель снимается по той же ссылке,
    // по которой вешался. Метод оборачиваем стрелкой, а не передаём напрямую:
    // debounce зовёт callback.apply(this), а у обработчика события this — это
    // window, и компонент потерял бы собственный контекст
    this.onResize = utils.debounce(() => this.updateScrollState(), 150).deferred;
  },
  mounted() {
    this.$nextTick(this.updateScrollState);
    window.addEventListener('resize', this.onResize);
    // на телефоне карточки берут пальцем: браузер сам drag-события из касаний
    // не порождает, вся разметка ниже рассчитана на мышь
    this.disableTouchDrag = enableTouchDrag(this.$el);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.disableTouchDrag();
  },
  methods: {
    stageLeads(stageId) {
      return this.leads.filter(lead => lead.stageId === stageId);
    },
    leadCountLabel(count) {
      return utils.pluralWord(count, 'заявка', 'заявки', 'заявок');
    },
    // fires when a stage's DraggableList receives a drop; itemsRaw for that
    // stage hasn't changed yet (leads is owned by the parent), so the moved
    // lead is simply the one whose stageId doesn't match this column yet
    onColumnChange(stageId, items) {
      const movedLead = items.find(lead => lead.stageId !== stageId);
      if (!movedLead) return;
      this.$emit('moveLead', { leadId: movedLead.id, stageId });
    },
    // DraggableList's own onDragEnter only re-indexes the drop position when
    // e.target === e.currentTarget — which a card's own nested content
    // (text, the ellipsis menu) almost never satisfies, so hovering over an
    // existing card would otherwise do nothing. Setting draggableIndex
    // directly sidesteps that check entirely: dragenter bubbles up from
    // whatever was actually hovered to this element's own listener, and we
    // already know the right index from the v-for.
    setHoverIndex(stageId, index, dragged) {
      this.draggedStageId = dragged?.stageId ?? null;
      this.hoveredStageId = stageId;
      if (this.isStageFrozen(stageId)) return;

      const [list] = this.$refs[`draggableList-${stageId}`] ?? [];
      if (list) list.draggableIndex = index;
    },
    // Колонка, из которой взяли карточку, во время переноса ПАЛЬЦЕМ замирает:
    // ни превью внутри неё, ни выбрасывание карточки в соседнюю колонку не
    // меняют её список. Причина — не косметика: Chrome шлёт pointercancel в
    // тот самый момент, когда узел, на котором началось касание, удаляют из
    // DOM, и жест умирает на полпути, не дойдя до drop (захват указателя от
    // этого не спасает — он перенаправляет доставку, но отменённый указатель
    // не воскрешает). Пока карточка остаётся в своей колонке — полупрозрачной,
    // как «взятая», — касание живо до самого конца.
    //
    // Мышь этим не затронута: там узел волен исчезать, и поведение доски
    // остаётся ровно прежним.
    isStageFrozen(stageId) {
      return isTouchDragging() && stageId === this.draggedStageId;
    },
    onBoardDragEnd() {
      this.hoveredStageId = null;
      this.draggedStageId = null;
    },
    scroll(direction) {
      // column width is responsive (see CSS), so measure the rendered column + gap
      // rather than relying on a fixed step. Without a rendered column there is
      // nothing to measure — and nothing to scroll past either, so just do nothing
      // ref внутри v-for — всегда массив, даже когда элемент один
      const [stage] = this.$refs.stage ?? [];
      if (!stage) return;
      const gap = parseFloat(getComputedStyle(this.$refs.board).columnGap) || 0;
      const step = stage.getBoundingClientRect().width + gap;
      this.$refs.viewport.scrollBy({ left: direction * step, behavior: 'smooth' });
    },
    updateScrollState() {
      const el = this.$refs.viewport;
      if (!el) return;
      this.canScrollLeft = el.scrollLeft > 0;
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    },
  },
};
</script>

<style scoped lang="scss">
$gap: 20px;

.crm-kanban {
  position: relative;
  // grid-template-columns: minmax(0, 1fr) is what stops the wide, non-wrapping
  // board below from forcing shrink-to-fit ancestors (.page__container, whose
  // width isn't stretched at this layout's ~1700-2300px breakpoint) wider than
  // the viewport: unlike flex/block, grid explicitly clamps a track's
  // contribution to the container's own intrinsic size to the minmax() floor,
  // regardless of the content inside it. The arrow buttons are
  // position: absolute so they don't take part in this grid layout.
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  &__scroll-btn {
    position: absolute;
    top: 6px;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--color-super-light);
    color: var(--color-middle-gray);
    cursor: pointer;
    transition: opacity 0.2s;

    &--left {
      left: 0;
    }

    &--right {
      right: 0;
    }

    svg {
      width: 16px;
      height: 16px;
      // arr-scroll.svg points down by default: rotate to point left / right
      transform: rotate(90deg);
    }

    &--right svg {
      transform: rotate(-90deg);
    }

    &:hover:not([disabled]) {
      opacity: 0.8;
    }

    &[disabled] {
      opacity: 0.25;
      cursor: default;
      pointer-events: none;
    }

    @media (max-width: 576px) {
      display: none;
    }
  }

  &__viewport {
    overflow-x: auto;
    overflow-y: hidden;
    // доску прокручивают стрелки по краям, полоса тут только съедала бы высоту
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__board {
    display: flex;
    gap: $gap;
    // не flex-start: колонки тянутся до высоты самой длинной, чтобы зона
    // приёма у коротких колонок доходила до низа доски
    align-items: stretch;
  }

  &__stage {
    display: flex;
    flex-direction: column;

    // phone (< 576px): fixed width, unchanged from the original design
    flex: 0 0 315px;
    width: 315px;

    // tablet and up to just below desktop: exactly 3 columns fit the board width
    @media (min-width: 576px) {
      flex-basis: calc((100% - #{$gap * 2}) / 3);
      width: calc((100% - #{$gap * 2}) / 3);
    }

    // desktop: exactly 4 columns fit the board width
    @media (min-width: 1024px) {
      flex-basis: calc((100% - #{$gap * 3}) / 4);
      width: calc((100% - #{$gap * 3}) / 4);
    }

    // wide desktop: exactly 5 columns fit the board width
    @media (min-width: 1600px) {
      flex-basis: calc((100% - #{$gap * 4}) / 5);
      width: calc((100% - #{$gap * 4}) / 5);
    }
  }

  &__stage-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    text-align: center;
    padding-bottom: 15px;
  }

  &__stage-title {
    font-weight: 500;
    font-size: 16px;
    color: var(--color-dark-gray);

    // не переносим на вторую строку: лишнее прячем за троеточием
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__stage-count {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__stage-line {
    height: 3px;
    border-radius: 7px;
    margin-bottom: 20px;
  }

  // корень DraggableList: лишнее звено между колонкой и списком карточек,
  // без растягивания оно обрезало бы зону приёма по высоте контента
  .kanban-stage-drop {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__stage-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    // вся свободная высота колонки — часть зоны приёма
    flex: 1;
    // доска, где пусты все колонки, иначе схлопнулась бы: тянуться до высоты
    // соседа тут не за кем, а целиться в зону приёма всё равно надо
    min-height: 200px;
  }

  &__lead {
    cursor: grab;

    &.drag {
      opacity: 0.5;
      cursor: grabbing;
    }
  }
}
</style>
